import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { isNil } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { RightAlignedButtons } from '../../components/button/aligned-buttons';
import { BaseButton } from '../../components/button/base-button';
import { CloseButton } from '../../components/button/close-button';
import { LoadingButton } from '../../components/button/loading-button';
import { CompatibleMachinesSelect } from '../../components/machine/scratchpad/compatible-machines-select';
import {
  SupportOpenSource,
  useSupportContext,
} from '../../components/support/support.context';
import { CalloutBanner } from '../../components/ui/callout-banner';
import { trackCopyScratchpad } from '../../config/analytics';
import i18n from '../../i18n';
import { logger } from '../../util/logger';
import {
  CreateScratchpadInput,
  ListScratchpadsDocument,
  Machine,
  Scratchpad,
  useCreateScratchpadMutation,
  useGetCompatibleMachinesForScratchpadLazyQuery,
  useGetScratchpadQuery,
} from '../graphql/generated';

export interface CopyScratchpadOptions {
  name: string;
  targetMachineId: string;
}

// Type for the partial machine data returned by the GraphQL query
export type CompatibleMachine = Pick<Machine, 'id' | 'name'>;

export interface UseCopyScratchpadResult {
  // Modal state
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  // Modal component
  CopyModal: (props: {
    scratchpad: Partial<Scratchpad>;
    onSuccess?: (copiedScratchpad: Scratchpad) => void;
  }) => JSX.Element | null;
}

interface CopyModalProps {
  scratchpad: Partial<Scratchpad>;
  onSuccess?: (copiedScratchpad: Scratchpad) => void;
  isModalOpen: boolean;
  closeModal: () => void;
}

// Modal component
const CopyModal = (props: CopyModalProps): ReactElement | null => {
  const { scratchpad, onSuccess, isModalOpen, closeModal } = props;

  const { open: openSupport } = useSupportContext();

  // Local state for the modal
  const [name, setName] = useState(
    scratchpad.name
      ? i18n.t('common:scratchpad_copy_name', { name: scratchpad.name })
      : ''
  );
  const [selectedMachineId, setSelectedMachineId] = useState<
    string | undefined
  >(scratchpad.machineId);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  // Fetch compatible machines using lazy query
  const [
    getCompatibleMachines,
    {
      data: compatibleMachinesData,
      loading: loadingMachines,
      error: errorMachinesGql,
    },
  ] = useGetCompatibleMachinesForScratchpadLazyQuery();

  const { data: scratchpadData, loading: loadingScratchpad } =
    useGetScratchpadQuery({
      variables: { input: { id: scratchpad.id as string } },
      fetchPolicy: 'cache-and-network',
      skip: isNil(scratchpad.id),
    });

  // Create scratchpad mutation
  const [createScratchpadMutation] = useCreateScratchpadMutation({
    refetchQueries: [ListScratchpadsDocument],
  });

  const errorMachines = errorMachinesGql?.message || null;
  const compatibleMachines =
    compatibleMachinesData?.getCompatibleMachinesForScratchpad?.machines;

  // Get the full scratchpad data with appData
  const fullScratchpad = scratchpadData?.getScratchpad;

  // Check if appData is valid for copying
  const hasValidAppData = !isNil(fullScratchpad?.appData);

  // Ensure the compatible machines are loaded one time
  useEffect(() => {
    if (scratchpad.id) {
      getCompatibleMachines({
        variables: { input: { scratchpadId: scratchpad.id } },
      });
    }
  }, [scratchpad.id, getCompatibleMachines]);

  // Auto-select first machine if only one available
  useEffect(() => {
    if (
      compatibleMachines &&
      compatibleMachines.length === 1 &&
      !selectedMachineId
    ) {
      setSelectedMachineId(compatibleMachines[0].id);
    }
  }, [compatibleMachines, selectedMachineId]);

  // Check if copy is possible
  const canCopy =
    name.trim().length > 0 &&
    !isNil(selectedMachineId) &&
    hasValidAppData &&
    !copyLoading;
  const showCopyButton = hasValidAppData || loadingScratchpad;

  const copyScratchpad = useCallback(
    async (options: CopyScratchpadOptions): Promise<Scratchpad | undefined> => {
      if (!scratchpad.id) {
        logger.error('no scratchpad id provided');
        setCopyError(i18n.t('common:scratchpad_copy_error'));
        return;
      }

      setCopyLoading(true);
      setCopyError(null);

      try {
        // Use the full scratchpad data with appData if available, otherwise fall back to the partial data
        const sourceScratchpad = fullScratchpad || scratchpad;

        // Prepare the input for creating a new scratchpad
        const createInput: CreateScratchpadInput = {
          name: options.name,
          description: sourceScratchpad.description,
          machineId: options.targetMachineId,
          appId: sourceScratchpad.appId,
          appData: sourceScratchpad.appData,
          bucket1Id: sourceScratchpad.bucket1Id,
        };

        const result = await createScratchpadMutation({
          variables: { input: createInput },
        });

        if (result.data?.createScratchpad) {
          // Track the successful copy using the source scratchpad's app slug
          trackCopyScratchpad(sourceScratchpad.app?.slug);
          return result.data.createScratchpad;
        } else {
          throw new Error('Failed to create scratchpad copy');
        }
      } catch (error) {
        setCopyError(i18n.t('common:scratchpad_copy_error'));
        logger.error('failed to copy scratchpad', {
          error,
          scratchpadId: scratchpad.id,
          options,
        });
      } finally {
        setCopyLoading(false);
      }
    },
    [scratchpad, fullScratchpad, createScratchpadMutation]
  );

  const handleCopy = useCallback(async () => {
    if (!canCopy || copyLoading || !selectedMachineId) {
      return;
    }

    const options = {
      name: name.trim(),
      targetMachineId: selectedMachineId,
    };

    const copiedScratchpad = await copyScratchpad(options);

    if (copiedScratchpad) {
      if (typeof onSuccess === 'function') {
        onSuccess(copiedScratchpad);
      }
      closeModal();
    }
  }, [
    canCopy,
    copyLoading,
    selectedMachineId,
    name,
    copyScratchpad,
    onSuccess,
    closeModal,
  ]);

  const handleDismiss = () => {
    closeModal();
  };

  const handleContactSupport = useCallback(async () => {
    await openSupport({ source: SupportOpenSource.MENU });
  }, [openSupport]);

  const primaryButton = useMemo(() => {
    if (showCopyButton) {
      return (
        <LoadingButton
          loading={copyLoading}
          buttonProps={{
            onClick: handleCopy,
            disabled: !canCopy,
          }}
        >
          {i18n.t('common:scratchpad_copy_button')}
        </LoadingButton>
      );
    } else {
      return (
        <BaseButton
          buttonProps={{
            onClick: handleContactSupport,
            color: 'primary',
          }}
        >
          {i18n.t('common:contact_support')}
        </BaseButton>
      );
    }
  }, [showCopyButton, copyLoading, handleCopy, canCopy, handleContactSupport]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <IonModal
      isOpen={isModalOpen}
      onDidDismiss={closeModal}
      canDismiss={true}
      backdropDismiss={true}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton fill="clear" onClick={handleDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>{i18n.t('common:scratchpad_copy_title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList className="ion-no-padding">
          <IonItem lines="full">
            <IonLabel position="stacked">
              {i18n.t('common:scratchpad_copy_name_label')}
            </IonLabel>
            <IonInput
              value={name}
              onIonInput={(e) => setName(e.detail.value!)}
              placeholder={i18n.t('common:scratchpad_copy_name_placeholder')}
              maxlength={255}
              disabled={copyLoading}
            />
          </IonItem>
          <CompatibleMachinesSelect
            loadingMachines={loadingMachines}
            errorMachines={errorMachines}
            compatibleMachines={compatibleMachines}
            currentMachineId={scratchpad?.machineId || ''}
            selectedMachineId={selectedMachineId}
            onMachineSelect={setSelectedMachineId}
            copyLoading={copyLoading}
            disabled={!canCopy}
          />
          {copyError && (
            <IonItem>
              <IonNote color="danger">{copyError}</IonNote>
            </IonItem>
          )}
        </IonList>
        {!hasValidAppData && (
          <CalloutBanner
            message={i18n.t('common:scratchpad_copy_no_app_data_message')}
            type="error"
            calloutBannerProps={{ className: 'ion-padding' }}
          />
        )}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <RightAlignedButtons>
            <CloseButton
              label={i18n.t('common:cancel')}
              onClose={handleDismiss}
            />
            {primaryButton}
          </RightAlignedButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export const useCopyScratchpadModal = (): UseCopyScratchpadResult => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal controls
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const result = useMemo(() => {
    return {
      // Modal state
      isModalOpen,
      openModal,
      closeModal,
      // Modal component
      CopyModal: (props: {
        scratchpad: Partial<Scratchpad>;
        onSuccess?: (copiedScratchpad: Scratchpad) => void;
      }) => (
        <CopyModal
          {...props}
          isModalOpen={isModalOpen}
          closeModal={closeModal}
        />
      ),
    };
  }, [isModalOpen, openModal, closeModal]);

  return result;
};
